import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo } from "react";
import { isWalletProviderDescriptor } from "@layerswap/wallet-core/types";
import { resolverService } from "../lib/resolvers/resolverService";
import { setExtendedRouteProviders } from "../lib/extendedRoutes";
export const isWalletProviderWithResolvers = (p) => !isWalletProviderDescriptor(p) && 'createConnection' in p;
export const extractExtendedRouteProviders = (providers) => providers
    .filter(isWalletProviderWithResolvers)
    .flatMap(p => p.extendedRouteProvider ?? []);
export const ResolverProviders = ({ children, walletProviders }) => {
    // Descriptors carry no resolvers; they re-enter this list as real
    // providers after `loadProvider()` resolves and `LayerswapProvider`
    // swaps them in.
    const realProviders = useMemo(() => walletProviders.filter(isWalletProviderWithResolvers), [walletProviders]);
    const transferProviders = useMemo(() => realProviders
        .map(provider => provider.transferProvider)
        .flat()
        .filter((provider) => Boolean(provider))
        .map(provider => provider()), [realProviders]);
    const gaslessProviders = useMemo(() => realProviders
        .map(provider => provider.gaslessProvider)
        .flat()
        .filter((provider) => Boolean(provider))
        .map(provider => provider()), [realProviders]);
    const contractAddressProviders = useMemo(() => realProviders
        .map(provider => provider.contractAddressProvider)
        .flat()
        .filter((provider) => Boolean(provider)), [realProviders]);
    const rpcHealthCheckProviders = useMemo(() => realProviders
        .map(provider => provider.rpcHealthCheckProvider)
        .flat()
        .filter((provider) => Boolean(provider)), [realProviders]);
    const balanceProviders = useMemo(() => realProviders
        .map(provider => provider.balanceProvider)
        .flat()
        .filter((provider) => Boolean(provider)), [realProviders]);
    const gasProviders = useMemo(() => realProviders
        .map(provider => provider.gasProvider)
        .flat()
        .filter((provider) => Boolean(provider)), [realProviders]);
    const nftProviders = useMemo(() => realProviders
        .map(provider => provider.nftProvider)
        .flat()
        .filter((provider) => Boolean(provider)), [realProviders]);
    // No ready-signal here: components gating on provider availability use
    // `useWalletProvidersReady()`, which tracks the wallet-connection
    // registry they actually read (see `WalletProvidersProvider`).
    useEffect(() => {
        resolverService.setProviders(balanceProviders, gasProviders, nftProviders, transferProviders, contractAddressProviders, rpcHealthCheckProviders, gaslessProviders);
        setExtendedRouteProviders(extractExtendedRouteProviders(walletProviders));
    }, [walletProviders, balanceProviders, gasProviders, nftProviders, transferProviders, contractAddressProviders, rpcHealthCheckProviders, gaslessProviders]);
    return _jsx(_Fragment, { children: children });
};
